import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import moment from "moment";

import MainLayout from "../../../component/atoms/layout/mainLayout";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import PretendardText from "../../../component/atoms/text/pretendardText";
import ExcelBtn from "./../../../component/atoms/button/excelBtn";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import TableTop from "./../../../component/atoms/layout/table/tableTop";
import SearchBar from "./../../../component/atoms/input/searchBar";
import GrayBtn from "./../../../component/atoms/button/grayBtn";
import DetailBtn from "../../../component/atoms/button/detailBtn";
import TableText from "../../../component/atoms/text/table/tableText";
import Td from "../../../component/atoms/layout/table/td";
import Table from "../../../component/atoms/layout/table/table";
import Tr from "./../../../component/atoms/layout/table/tr";
import Th from "./../../../component/atoms/layout/table/th";
import Checkbox from "./../../../component/atoms/input/checkbox";
import RedBtn from "./../../../component/atoms/button/redBtn";

import TableCount from "./../../../component/molecules/tableCount";
import Pagination from "./../../../component/molecules/pagination";
import SubSideBarItem from "../../../component/molecules/subSideBarItem";

import Header from "../../../component/organisms/header";
import Sidebar from "../../../component/organisms/sidebar";
import SubSideBar from "../../../component/organisms/subSideBar";

import Modal from "./../../../component/templates/Modal";
import CategoryManagePopup from "../../../component/templates/community/categoryManagePopup";

import { toastMsg } from "../../../util/toastMsg";
import { errorHandler } from "../../../util/errorHandler";
import scrollToTop from "../../../util/scrollToTop";
import { formatNumberWithCommas } from "./../../../util/formatNumber";
import BoardApi from "../../../api/board.api";
import { route } from "./../../../router/route";
import Empty from "../../../component/molecules/empty";

// sessionStorage 초기화
function sessionStorageInit() {
  sessionStorage.removeItem("sCheckedListStorage");
  const event = new Event("sessionStorageChange");
  window.dispatchEvent(event);
}

const CommunityManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line no-unused-vars
  const [isWriteAuthority, setIsWriteAuthority] = useState(false);

  // ---------- 쓰기 권한체크 ---------- //
  useEffect(() => {
    const writeMenu = sessionStorage.getItem("writeMenu");

    if (writeMenu?.includes(route.community_management)) {
      setIsWriteAuthority(true);
    } else {
      setIsWriteAuthority(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [keyword, setKeyword] = useState("");
  const [selectedMainCategory, setSelectedMainCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [activationFilter, setActivationFilter] = useState(true);

  const page = useMemo(() => {
    return searchParams.get("page") ? searchParams.get("page") : 1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 10, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 0,
    first: false,
    last: false,
  });

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    getMainCategoryList();

    return () => {
      sessionStorage.removeItem("sCheckedListStorage");
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    setKeyword(searchParams.get("keyword") ? searchParams.get("keyword") : "");
    setSelectedMainCategory(
      searchParams.get("mainCategory")
        ? Number(searchParams.get("mainCategory"))
        : ""
    );
    setSelectedSubCategory(
      searchParams.get("subCategory")
        ? Number(searchParams.get("subCategory"))
        : ""
    );
    setActivationFilter(
      searchParams.get("activation") === "false" ? false : true
    );

    getCommunityList();
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 카테고리 설정 팝업 관련 ---------- //
  const [popupOpen, setPopupOpen] = useState(false);

  // ---------- 커뮤니티 관련 ---------- //
  const [communityList, setCommunityList] = useState([]);

  const getCommunityList = async () => {
    try {
      const data = {
        page: Number(page),
        size: Number(pagination.size),
        boardPostType: "COMMUNITY",
      };

      if (keyword) {
        data.keyword = searchParams.get("keyword");
      }

      if (searchParams.get("mainCategory")) {
        data.boardCategoryId = Number(searchParams.get("mainCategory"));
      }

      if (searchParams.get("subCategory")) {
        data.boardSubCategoryId = Number(searchParams.get("subCategory"));
      }

      if (searchParams.get("activation") === "false") {
        data.activation = "Inactive";
      }

      const response = (await BoardApi.getListBoard(data)).data.data;

      const processedResponse = [...response.content].map((item, index) => {
        return {
          ...item,
          numbering: response.totalCount - (page - 1) * pagination.size - index,
        };
      });

      setCommunityList(processedResponse);
      setPagination({
        ...pagination,
        totalCount: response.totalCount,
        totalPages: Math.ceil(response.totalCount / 10),
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  // ---------- 상위 카테고리 관련 ---------- //
  const [mainCategoryList, setMainCategoryList] = useState([]);

  // 상위 카테고리 가져오기
  const getMainCategoryList = async () => {
    try {
      const data = {
        page: 1,
        size: 0,
        sort: "orderNumber",
        orderBy: "ASC",
      };

      const response = (await BoardApi.getListMain(data)).data.data.content;

      const processedResponse = response.map((item) => {
        return {
          ...item,
          dropdownOpen: false,
          popupDropdownOpen: false,
        };
      });

      const newArray = [...processedResponse];

      const targetIndex = newArray.findIndex((item) => item.id === 7);

      if (targetIndex !== -1) {
        const [item] = newArray.splice(targetIndex, 1);

        newArray.push(item);
      }

      setMainCategoryList(newArray);
      // 상태 업데이트
      const newDataTemp = {
        boardCategoryList: newArray,
      };
      await BoardApi.updateMain(newDataTemp);
    } catch (error) {
      errorHandler(error);
    }
  };

  const sessionStorageCheckedList = sessionStorage.getItem(
    "sCheckedListStorage"
  );
  const [checkedListStorage, setCheckedListStorage] = useState([]);
  const [buttonGroupOpen, setButtonGroupOpen] = useState(false);
  const [isAllCheck, setIsAllCheck] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      setCheckedListStorage(
        sessionStorageCheckedList ? JSON.parse(sessionStorageCheckedList) : []
      );
    };

    window.addEventListener("sessionStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("sessionStorageChange", handleStorageChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sessionStorage.setItem(
      "sCheckedListStorage",
      JSON.stringify(checkedListStorage)
    );
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (communityList.length > 0) {
      const idsInArray1 = new Set(checkedListStorage.map((item) => item.id));

      const allIdsIncluded = communityList.every((item) =>
        idsInArray1.has(item.id)
      );

      setIsAllCheck(allIdsIncluded);
    }

    if (checkedListStorage.length > 0) {
      setButtonGroupOpen(true);
    } else {
      setButtonGroupOpen(false);
    }
  }, [checkedListStorage, communityList]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const deleteHandler = async () => {
    try {
      for (let i = 0; i < checkedListStorage.length; i++) {
        await BoardApi.deleteBoard(checkedListStorage[i].id);
      }
      sessionStorageInit();
      setDeleteModalOpen(false);
      toast(toastMsg.delete);
      getCommunityList();

      navigate(
        `${route.community_management}?page=1&mainCategory=${selectedMainCategory}&subCategory=${selectedSubCategory}&keyword=${keyword}&activation=${activationFilter}`
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  // ---------- 활성 상태 관련 ---------- //
  const [filterOpen, setFilterOpen] = useState(false);

  const activationChangeFunc = async (value) => {
    try {
      const data = {
        activation: value,
      };

      for (let i = 0; i < checkedListStorage.length; i++) {
        await BoardApi.updateActivationBoard(checkedListStorage[i].id, data);
      }

      sessionStorageInit();
      toast(toastMsg.update);
      getCommunityList();

      navigate(
        `${route.community_management}?page=1&mainCategory=${selectedMainCategory}&subCategory=${selectedSubCategory}&keyword=${keyword}&activation=${activationFilter}`
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 카테고리 영역 */}
        <SubSideBar title="카테고리">
          <SubSideBarItem
            selected={selectedMainCategory === ""}
            onClick={() => {
              if (selectedMainCategory === "") return;

              sessionStorageInit();
              navigate(
                `${route.community_management}?page=1&mainCategory=&subCategory=&keyword=&activation=`
              );
            }}
          >
            전체
          </SubSideBarItem>
          {mainCategoryList.map((item, index) => {
            return (
              <React.Fragment key={index}>
                <SubSideBarItem
                  onClick={() => {
                    if (selectedMainCategory === item.id) return;

                    sessionStorageInit();
                    navigate(
                      `${route.community_management}?page=1&mainCategory=${item.id}&subCategory=&keyword=&activation=`
                    );
                  }}
                  selected={selectedMainCategory === item.id}
                  dropdown={item.id !== 1 && item.id !== 2 && item.id !== 7}
                  isRotated={item.dropdownOpen}
                  dropdownHandler={() => {
                    const updatedMainCategoryList = mainCategoryList.map(
                      (category) => {
                        if (category.id === item.id) {
                          return {
                            ...category,
                            dropdownOpen: !category.dropdownOpen,
                          };
                        }

                        return category;
                      }
                    );

                    setMainCategoryList(updatedMainCategoryList);
                  }}
                >
                  {item.title}
                </SubSideBarItem>
                {item.dropdownOpen &&
                  item.boardSubCategoryList?.map((subItem, subIndex) => {
                    return (
                      <SubSideBarItem
                        key={subIndex}
                        reply={true}
                        selected={selectedSubCategory === subItem.id}
                        onClick={() => {
                          if (selectedSubCategory === subItem.id) return;

                          sessionStorageInit();
                          navigate(
                            `${route.community_management}?page=1&mainCategory=${item.id}&subCategory=${subItem.id}&keyword=&activation=`
                          );
                        }}
                      >
                        {subItem.title}
                      </SubSideBarItem>
                    );
                  })}
              </React.Fragment>
            );
          })}

          <SubSideBarFooter>
            <SubSideBarFooterText>카테고리 관리</SubSideBarFooterText>
            <img
              src="/assets/admin/icons/ico_setting.svg"
              style={{
                width: 13,
                height: 13,
                cursor: "pointer",
              }}
              onClick={() => {
                if (isWriteAuthority) {
                  setPopupOpen(true);
                } else {
                  toast("권한이 없습니다.");
                }
              }}
              alt="카테고리 관리"
            />
          </SubSideBarFooter>
        </SubSideBar>

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle>커뮤니티 관리</TableTitle>
          {/* 테이블 상단 영역 */}
          <TableTop>
            <TableCount text={"게시글"} count={pagination.totalCount} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <SearchBar
                value={keyword}
                placeholder={"제목, 닉네임"}
                onChange={(e) => {
                  setKeyword(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    sessionStorageInit();
                    navigate(
                      `${route.community_management}?page=1&mainCategory=${selectedMainCategory}&subCategory=${selectedSubCategory}&keyword=${keyword}&activation=`
                    );
                  }
                }}
                onClick={() => {
                  sessionStorageInit();
                  navigate(
                    `${route.community_management}?page=1&mainCategory=${selectedMainCategory}&subCategory=${selectedSubCategory}&keyword=${keyword}&activation=`
                  );
                }}
              />
              {/* <ExcelBtn /> */}
            </div>
          </TableTop>

          {/* 버튼 그룹 */}
          {buttonGroupOpen && isWriteAuthority && (
            <div
              style={{
                width: "100%",
                height: 49,
                display: "flex",
                gap: 12,
              }}
            >
              {activationFilter ? (
                <GrayBtn
                  onClick={() => {
                    activationChangeFunc(false);
                  }}
                >
                  게시글 숨기기
                </GrayBtn>
              ) : (
                <GrayBtn
                  onClick={() => {
                    activationChangeFunc(true);
                  }}
                >
                  게시글 숨김 풀기
                </GrayBtn>
              )}

              <RedBtn
                style={{
                  backgroundColor: "#272829",
                }}
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
              >
                삭제
              </RedBtn>
            </div>
          )}

          {/* 테이블 영역 */}
          <Table>
            <colgroup>
              {/* 합계: 1044px */}
              {/* 체크박스 */}
              <col width="56px" />
              {/* No */}
              <col width="40px" />
              {/* 제목 */}
              <col width="265px" />
              {/* 닉네임 */}
              <col width="114px" />
              {/* 카테고리 */}
              <col width="123px" />
              {/* 등록일 */}
              <col width="122px" />
              {/* 조회수 */}
              <col width="72px" />
              {/* 댓글 */}
              <col width="59px" />
              {/* 상태 */}
              <col width="101px" />
              {/* 보기 */}
              <col width="92px" />
            </colgroup>
            <thead>
              <Tr style={{ height: "41px", background: "#272829" }}>
                <Th style={{ paddingLeft: "20px" }}>
                  <Checkbox
                    checked={isAllCheck}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // 배열 병합 및 중복 제거
                        const mergedArray = [
                          ...communityList,
                          ...checkedListStorage,
                        ].reduce((acc, current) => {
                          const existingItem = acc.find(
                            (item) => item.id === current.id
                          );
                          if (!existingItem) {
                            acc.push(current);
                          }
                          return acc;
                        }, []);

                        setCheckedListStorage(mergedArray);
                      } else {
                        const idsToExclude = new Set(
                          communityList.map((item) => item.id)
                        );
                        const filteredArray = checkedListStorage.filter(
                          (item) => !idsToExclude.has(item.id)
                        );

                        setCheckedListStorage(filteredArray);
                      }
                    }}
                  />
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    No
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    제목
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    닉네임
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    카테고리
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    등록일
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    조회수
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    댓글
                  </TableText>
                </Th>
                <Th>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <TableText
                      style={{
                        fontWeight: "600",
                        lineHeight: "22.75px",
                      }}
                    >
                      상태
                    </TableText>
                    <img
                      className={filterOpen === true ? "rotate" : "rotate-back"}
                      src="/assets/admin/icons/ico_arrow_down.svg"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setFilterOpen(!filterOpen);
                      }}
                      alt="필터 펼치기/접기"
                    />
                    {filterOpen && (
                      <FilterWrapper>
                        <FilterTextWrapper>
                          <FilterText
                            onClick={() => {
                              navigate(
                                `${
                                  route.community_management
                                }?page=1&mainCategory=${selectedMainCategory}&subCategory=${selectedSubCategory}&keyword=${keyword}&activation=${true}`
                              );
                            }}
                          >
                            활성
                            {activationFilter ? (
                              <img
                                src="/assets/admin/icons/ico_selectbox_checked.svg"
                                alt="선택됨"
                              />
                            ) : null}
                          </FilterText>
                        </FilterTextWrapper>
                        <FilterText
                          onClick={() => {
                            navigate(
                              `${
                                route.community_management
                              }?page=1&mainCategory=${selectedMainCategory}&subCategory=${selectedSubCategory}&keyword=${keyword}&activation=${false}`
                            );
                          }}
                        >
                          숨김
                          {activationFilter ? null : (
                            <img
                              src="/assets/admin/icons/ico_selectbox_checked.svg"
                              alt="선택됨"
                            />
                          )}
                        </FilterText>
                      </FilterWrapper>
                    )}
                  </div>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    보기
                  </TableText>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {communityList.map((item, index) => {
                return (
                  <Tr
                    key={index}
                    style={{
                      height: "59px",
                      background: "transparent",
                      borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    <Td style={{ paddingLeft: "20px" }}>
                      <Checkbox
                        checked={checkedListStorage.some(
                          (item02) => item02.id === item.id
                        )}
                        onChange={() => {
                          if (
                            checkedListStorage.length > 0 &&
                            checkedListStorage.some(
                              (item02) => item02.id === item.id
                            )
                          ) {
                            let temp = checkedListStorage.filter(
                              (item02) => item02.id !== item.id
                            );
                            setCheckedListStorage(temp);
                          } else {
                            setCheckedListStorage([
                              item,
                              ...checkedListStorage,
                            ]);
                          }
                        }}
                      />
                    </Td>
                    <Td>
                      <TableText>{item.numbering}</TableText>
                    </Td>
                    {/* 제목 */}
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "248px",
                        }}
                      >
                        {item.title}
                      </TableText>
                    </Td>
                    {/* 닉네임 */}
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "98px",
                        }}
                      >
                        {item.member?.nickname ? item.member?.nickname : ""}
                      </TableText>
                    </Td>
                    {/* 카테고리 */}
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "103px",
                        }}
                      >
                        {item.boardSubCategory
                          ? item.boardSubCategory?.title
                          : item.boardCategory.title}
                      </TableText>
                    </Td>
                    {/* 등록일 */}
                    <Td>
                      <TableText>
                        {moment(item.createdAt).format("YYYY-MM-DD")}
                      </TableText>
                    </Td>
                    {/* 조회수 */}
                    <Td>
                      <TableText>
                        {formatNumberWithCommas(item.viewCount)}
                      </TableText>
                    </Td>
                    {/* 댓글 */}
                    <Td>
                      <TableText>
                        {formatNumberWithCommas(item.commentCount)}
                      </TableText>
                    </Td>
                    {/* 상태 */}
                    <Td>
                      <TableText
                        style={{
                          color: `${item.activation ? "#aaa" : "#cf6679"}`,
                        }}
                      >
                        {item.activation ? "활성" : "숨김"}
                      </TableText>
                    </Td>
                    {/* 보기 */}
                    <Td>
                      <DetailBtn
                        onClick={() => {
                          navigate(
                            `${route.community_management_detail}/${item.id}`
                          );
                          scrollToTop();
                        }}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          {communityList.length > 0 ? null : <Empty />}

          {/* 페이지네이션 영역 */}
          <Pagination
            route={route.community_management}
            queryString={`mainCategory=${selectedMainCategory}&subCategory=${selectedSubCategory}&keyword=${keyword}&activation=${activationFilter}`}
            currentPage={page}
            totalPages={pagination.totalPages}
            rangeSize={pagination.rangeSize}
            first={pagination.first}
            last={pagination.last}
          />
        </DetailPageLayout>
      </MainLayout>

      {deleteModalOpen && (
        <Modal
          type="negative"
          title="삭제하시겠습니까?"
          text={
            <>
              삭제된 데이터는 복구가 불가합니다.
              <br />
              그래도 삭제하시겠습니까?
            </>
          }
          setModalOpen={setDeleteModalOpen}
          onClick={deleteHandler}
        />
      )}

      {/* 카테고리 관리 팝업 */}
      {popupOpen && (
        <CategoryManagePopup
          popupOpen={popupOpen}
          setPopupOpen={setPopupOpen}
          getMainCategoryList={getMainCategoryList}
          mainCategoryList={mainCategoryList}
          setMainCategoryList={setMainCategoryList}
        />
      )}
    </>
  );
};

export default CommunityManagement;

const FilterWrapper = styled.div.attrs((props) => {})`
  padding: 10px;
  border-radius: 3px;
  background: #272829;
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 48px;
  z-index: 5;
`;

const FilterTextWrapper = styled.div.attrs((props) => {})`
  display: flex;
  gap: 1px;
`;

const FilterText = styled(PretendardText).attrs((props) => {})`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 10px;
  color: #aaa;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background: #313233;
  }
`;

const SubSideBarFooter = styled.div.attrs((props) => {})`
  margin-top: 5px;
  width: 100%;
  height: 38px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0px 16px;
  border-top: 0.5px solid var(--outline1, rgba(255, 255, 255, 0.12));
`;

const SubSideBarFooterText = styled(PretendardText).attrs((props) => {})`
  font-weight: 500;
  font-size: 13px;
  line-height: 18.75px;
  color: #aaa;
`;
